import type { TFunction } from "i18next";
import { AiUsageFunction, type AuditLogDetailsDto } from "../../../../../autogen/bff-api";
import { aiUsageFunctionLabels } from "../../../../admin/org/aiFeatureLabels";

const CHANGE = "change";

export const eventLabels = (t: TFunction): Record<string, string> => ({
  [CHANGE]: t("Changed"),
  "person.signed_in.email": t("Signed in with email"),
  "person.signed_in.entra_id": t("Signed in with Entra ID"),
  "support_access.started": t("Support access started"),
  "access.given": t("Access given"),
  "access.removed": t("Access removed"),
  "tender.opened": t("Tender opened"),
  "tender.submitted": t("Tender submitted"),
  "tender.changed": t("Tender changed"),
  "tender.withdrawn": t("Tender withdrawn"),
  "document.downloaded": t("Document downloaded"),
  exported: t("Exported"),
  "ai.used": t("AI used"),
  "ai.suggestion.inserted": t("AI suggestion inserted"),
  "ai.assistant.proposed": t("Assistant proposed an action"),
  "ai.assistant.confirmed": t("Assistant action confirmed"),
  "ai.assistant.rejected": t("Assistant action rejected"),
  "ai.assistant.executed": t("Assistant action carried out"),
  "ai.assistant.failed": t("Assistant action failed"),
  "procurement.change.published": t("Procurement change published"),
  "procurement.status.PublicationPending": t("Procurement sent for publication"),
  "procurement.status.PublicationFailed": t("Procurement publication failed"),
  "procurement.status.Published": t("Procurement published"),
  "procurement.status.Evaluation": t("Evaluation started"),
  "procurement.status.Evaluated": t("Evaluation completed"),
  "procurement.status.AwardDecision": t("Award decision made"),
  "procurement.status.Awarded": t("Procurement awarded"),
  "procurement.status.Cancelled": t("Procurement cancelled"),
  "procurement.status.Closed": t("Procurement closed"),
  "procurement.status.Completed": t("Procurement completed"),
  "procurement.status.Reopened": t("Procurement reopened"),
  "market_dialogue.sent_for_publication.E2": t("Market dialogue sent for publication (below the EEA threshold, E2)"),
  "market_dialogue.sent_for_publication.4": t("Market dialogue sent for publication (FOA, subtype 4)"),
  "market_dialogue.sent_for_publication.5": t("Market dialogue sent for publication (utilities regulation, subtype 5)"),
  "market_dialogue.published": t("Market dialogue published"),
  "market_dialogue.opened_to_invited": t("Market dialogue opened to invited suppliers"),
  "market_dialogue.publication_failed": t("Market dialogue publication failed"),
  "tender.status.Submitted": t("Tender submitted"),
  "tender.status.Withdrawn": t("Tender withdrawn"),
  "tender.status.Awarded": t("Tender awarded"),
  "tender.status.Rejected": t("Tender rejected"),
  "tender.status.AwardRevoked": t("Tender award revoked"),
  "tender.status.RejectionRevoked": t("Tender rejection revoked"),
  "need.status.DRAFT": t("Need saved as draft"),
  "need.status.REPORTED": t("Need reported"),
  "need.status.SENT_BACK": t("Need sent back"),
  "need.status.APPROVED": t("Need approved"),
  "need.status.PLANNED": t("Need planned"),
  "need.status.FRAMEWORK_RECOMMENDED": t("Framework agreement recommended for need"),
  "need.status.PROCUREMENT_CREATED": t("Procurement created from need"),
  "need.status.UNDER_PROCUREMENT": t("Need under procurement"),
  "need.status.UNDER_CALL_OFF": t("Need under call-off"),
  "need.status.COMPLETED": t("Need completed"),
  "need.status.REJECTED": t("Need rejected"),
  "need.status.WITHDRAWN": t("Need withdrawn"),
  "contract_task.TitleChange": t("Contract task title changed"),
  "contract_task.DescriptionChange": t("Contract task description changed"),
  "contract_task.DueDateChange": t("Contract task due date changed"),
  "contract_task.StatusChange": t("Contract task status changed"),
  "contract_task.ResponsiblePartyChange": t("Contract task responsible changed"),
});

export const itemTypeLabels = (t: TFunction): Record<string, string> => ({
  Procurement: t("Procurement"),
  Tender: t("Tender"),
  Need: t("Need"),
  Contract: t("Contract"),
  ContractTask: t("Contract task"),
  Person: t("User"),
  Document: t("Document"),
  SignedDocument: t("Signed document"),
  DocumentMemory: t("Document chat"),
  Project: t("Project"),
  Organization: t("Organization"),
  Department: t("Department"),
  FrameworkAgreement: t("Framework agreement"),
  NegotiationRound: t("Negotiation round"),
  Export: t("Export"),
  Conversation: t("Conversation with the assistant"),
  SavedReport: t("Saved report"),
  MarketDialogue: t("Market dialogue"),
  Letter: t("Award letter"),
  Dep: t("Department"),
  Upload: t("Uploaded file"),
  Doc: t("Document"),
  TextDocument: t("Text document"),
  Comment: t("Comment"),
  Question: t("Question"),
  Connection: t("Entra ID connection"),
  User: t("User"),
});

export const exportLabels = (t: TFunction): Record<string, string> => ({
  contract_list: t("Contract list"),
  contract_task_list: t("Task list"),
  procurement_list: t("Procurement list"),
  qa_report: t("Q&A report"),
  procurement_protocol: t("Procurement protocol"),
  project_procurement_summary: t("Project procurement summary"),
  text_document: t("Text document"),
  ifrs16_report: t("IFRS 16 report"),
  audit_log: t("Audit log"),
});

const changeLabel = (t: TFunction, itemType: string): string | undefined => {
  const item = itemTypeLabels(t)[itemType];
  return item ? String(t("{{item}} changed", { item })) : undefined;
};

export const auditEventLabel = (t: TFunction, eventType: string, itemType?: string): string =>
  (eventType === CHANGE && itemType ? changeLabel(t, itemType) : undefined) ?? eventLabels(t)[eventType] ?? eventType;

export const auditItemTypeLabel = (t: TFunction, type: string): string =>
  itemTypeLabels(t)[type] ?? String(t("Item"));

export const auditExportLabel = (t: TFunction, kind: string): string => exportLabels(t)[kind] ?? kind;

export const roleLabels = (t: TFunction): Record<string, string> => ({
  Owner: t("Owner"),
  Admin: t("Admin"),
  SourcingCreator: t("Sourcing Creator"),
  SourcingViewer: t("Sourcing Viewer"),
  ContractCreator: t("Contract Creator"),
  ContractViewer: t("Contract Viewer"),
  ProjectCreator: t("Project Creator"),
  ProjectViewer: t("Project Viewer"),
  Editor: t("Editor"),
  Viewer: t("Viewer"),
  Counterparty: t("Counterparty"),
});

export const scopeTypeLabels = (t: TFunction): Record<string, string> => ({
  organization: t("Organization"),
  department: t("Department"),
  event: t("Procurement"),
  contract: t("Contract"),
});

export const removalReasonLabels = (t: TFunction): Record<string, string> => ({
  "Role removed": t("The role was removed"),
  "Person removed from the organization": t("The person was removed from the organisation"),
  "Ownership transferred": t("Ownership was transferred"),
  "Removed by department administration": t("Removed in the department administration"),
  "Last standing organization access removed": t("The last access to the organisation was removed"),
  "Entra default role changed": t("The default role from Entra ID was changed"),
  "Roles set by hand": t("The roles were set by hand"),
  "Entra group membership removed": t("The membership of the Entra ID group ended"),
  "Directory user deprovisioned": t("The user was removed from Entra ID"),
  "Removed from the contract": t("Removed from the contract"),
  "Draft deleted": t("The draft was deleted"),
  unshared: t("Sharing was stopped"),
});

export const aiFunctionLabels = (t: TFunction): Record<AiUsageFunction, string> => ({
  ...aiUsageFunctionLabels(t),
  [AiUsageFunction.CpvSuggestions]: t("Suggest CPV codes with AI"),
  [AiUsageFunction.NeedTypeSuggestion]: t("Suggest type & priority with AI"),
  [AiUsageFunction.Autofill]: t("Autofill document"),
});

export const auditDetailLines = (t: TFunction, details: AuditLogDetailsDto | null | undefined): string[] => {
  if (!details) return [];
  const scopeKind = details.scopeType ? (scopeTypeLabels(t)[details.scopeType] ?? details.scopeType) : null;
  const scope = scopeKind && details.scopeName ? `${scopeKind} «${details.scopeName}»` : scopeKind;
  const ownerKind = details.belongsTo ? auditItemTypeLabel(t, details.belongsTo.type) : null;
  const owner = ownerKind && details.belongsTo?.name ? `${ownerKind} «${details.belongsTo.name}»` : ownerKind;
  return [
    details.feature &&
      String(t("AI function: {{feature}}", { feature: aiFunctionLabels(t)[details.feature] ?? details.feature })),
    owner && String(t("Belongs to: {{owner}}", { owner })),
    details.role && String(t("Role: {{role}}", { role: roleLabels(t)[details.role] ?? details.role })),
    scope && String(t("Applies to: {{scope}}", { scope })),
    details.reason &&
      String(t("Reason: {{reason}}", { reason: removalReasonLabels(t)[details.reason] ?? details.reason })),
    details.proposed && String(t("Proposed: {{values}}", { values: details.proposed })),
    details.confirmed && String(t("Confirmed: {{values}}", { values: details.confirmed })),
  ].filter((line): line is string => !!line);
};
